// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react'
import { toast } from 'react-toastify';

import api from '../../../api';

// Components
import Search from '../../Search';
import PlanCard from './PlanCard';

// Styles
import '../../../styles/planeditor.css';
import PlanPage from './PlanPage';
import PageNavigator from './PageNavigator';

import download from '../../../utilities/downloadable'
import Select from 'react-dropdown-select'
import {options} from './options';
import * as planSync from '../../../utilities/planSync';
import CxReviewPane from './CxReviewPane';

const RESULT_LIMIT = 10;

const newPlan = {
    internalPlanId: "",
    planName: "",
    directory: "",
    active: true,
    endMm: 12,
    endDd: 31
};

export default class PlanEditor extends React.Component {
    abortController = new AbortController()

    state = {
        // Search for plans, accounts, etc.
        query: "",
        isSearching: false,

        // Plans
        plans: [],
        selectedPlanIdx: 0, // Index of selected plan in plans array

        // We only pull the plans to display on the front-end. This means we need to keep track of the page number as well
        planPage: 0,

        // We also need to keep track of the total number of plans in the system
        planCount: 0,

        // Editing (or new) plan object
        editingPlan: null, // If null, we're not creating a new plan
        /* {
            internalPlanId: string,
            planName: string,
            directory: string,
            active: bool,
            endMm: int, // Default 12
            endDd: int, // Default 31
        } */
        // If we're creating a new plan, we need to make sure that we unselect any actively editing plan (set seletedPlanIdx to null)

        filters: {
            active: null
        },

        // CX plan sync review mode
        cxStatus: null,        // /tpa/plansync/status response (null = not linked / not loaded)
        cxPending: [],         // /tpa/plansync/pending rows
        cxMode: false,         // false = All Plans, true = CX Review
        selectedPendingIdx: 0
    }

    componentDidMount = async ()  => {
        // Should we load a query from the URL?
        const queryParam = new URLSearchParams(window.location.search).get('search');
        if (queryParam) {
            this.setState({ query: queryParam });
        }

        // Deep link from Settings → Sync "Review Queue"
        if (new URLSearchParams(window.location.search).get('cxreview'))
            this.setState({ cxMode: true });

        await this.init(this.props.team)
    }

    componentWillUnmount = () => {
        this.searchTimeout && clearTimeout(this.searchTimeout); 
    }

    init = async (team) => {
        await this.loadCxPending();
        await this.loadPlans();

        // setTimeout(()=>{
        //     this.checkImportStatus()
        // }, 3000)
        await this.checkImportStatus();
    }

    componentDidUpdate = async (next) => {
        if (this.props.team?._id !== next.team?._id)
            await this.init(next.team);
    }

    // Load plans (and provide search/filter query)
    loadPlans = async (page=0, searchCall=false) => {

        // Abort the last request for loading in plans
        this.abortController.abort()
        this.abortController = new AbortController()


        // Make sure the page number is positive
        // this.props.loading(999, 'Loading plans...', false, 'planEditor');
        if (page < 0) page = 0;

        // Make sure we're not trying to fetch more plans than what exist
        // if (page * RESULT_LIMIT >= this.state.planCount) return;

        // Set page number to state
        this.setState({
            planPage: page,
            isSearching: true
        });

        // Load plans from the API with query and pagination parameters
        const res = await api.get(`/tpa/plan/list?resultLimit=10&search=${encodeURIComponent(this.state.query)}&resultOffset=${page * RESULT_LIMIT}&filterActive=${this.state.filters?.active}`, {signal: this.abortController.signal}, false, {}, true);

        if(!res || (!res?.success && res.cancelled))
            return

        if (!res?.success) {
            toast.error("Failed to fetch TPA plans.");
            this.setState({ isSearching: false });
            this.props.loaded('planEditor')
            return;
        }

        let numPlans = res.totalPlanLength

        if (searchCall === true && res.totalPlanLength === 0) {
            numPlans = -1
        } 

        // Set plans to state
        this.setState({
            plans: res.plans,
            planCount: numPlans,
            isSearching: false,
            selectedPlanIdx: 0,
            editingPlan: res.plans[0]
        });

        if(!this.props.team?.isSyncingPlanImport)
            this.props.loaded('planEditor')
    }

    // Load CX plan sync status + pending inbox. Toggle renders only when connected.
    loadCxPending = async () => {
        const status = await planSync.getStatus();
        if (!status?.success || !status.connected)
            return this.setState({ cxStatus: null, cxPending: [], cxMode: false });

        const res = await planSync.getPending();
        this.setState({
            cxStatus: status,
            cxPending: res?.success ? (res.pending ?? []) : [],
            selectedPendingIdx: Math.min(
                this.state.selectedPendingIdx,
                Math.max((res?.success ? (res.pending ?? []).length : 0) - 1, 0)
            )
        });
    }

    // Resolve one or many pending rows, then refetch inbox + plan list so
    // badges and counts stay fresh.
    resolveCx = async (body) => {
        const res = await planSync.resolve(body);
        if (!res?.success) return res; // api.js already toasted the error
        await this.loadCxPending();
        await this.loadPlans(this.state.planPage);
        return res;
    }

    // Manipulators for state managed at this level
    // state.query
    // Search for plans, accounts, etc. using the query
    search = async (query) => {
        this.setState({ query });

        // If we've already set a timeout, clear it and overwrite
        if (this.searchTimeout)
            clearTimeout(this.searchTimeout);

        this.searchTimeout = setTimeout(async () => 
            {
                await this.loadPlans(0, true)
                // this.props.loaded()
            }, 300);
    }

    // state.plans
    // Edit a specific field of a plan in state (not cloud synced)
    editPlanField = (field, value) => {

        // Must have editing object
        if (!this.state.editingPlan) return;

        this.setState({
            editingPlan: {
                ...this.state.editingPlan,
                [field]: value
            }
        });
    }

    // state.filters
    // Update certain fields for filtering
    editFilter = (field, value) => {
        this.setState({
            [field]: value
        })
    }

    // If `state.editingPlan._id` doesn't exist, this is a new plan, we need to add it. If not, we need to update the selected plan.
    savePlan = async () => {
        const s = this.state;
        const e = s.editingPlan;

        // We need to make sure that that the plan has a valid ID and name
        if (!e?.internalPlanId)
            return toast.error("Plan must have a valid ID.");

        for(const key of Object.keys(e)){
            if(typeof e[key] === "string")
                e[key] = e[key].trim();
        }

        // This is a new plan - create it
        if (!s.editingPlan._id) {
            this.props.loading(999, '', false, 'planEditor');
            const res = await api.post('/tpa/plan/create', { plan: e });

            if (!res?.success || !res?.data) {
                this.props.loaded('planEditor');
                return;
            }

            this.setState({
                plans: [ res.data, ...s.plans ],
                editingPlan: [res.data, ...s.plans][0], //TODO: Fix so that once created editingPlan should show planIdx=0
                selectedPlanIdx: 0,
                planCount: s.planCount + 1
            });
            this.props.loaded('planEditor');
            return;
        }

        // Not a new plan, edit existing
        // Make a deep copy of the editing plan values and the old values
        const plan = { ...e };
        const old = { ...s.plans[s.selectedPlanIdx] };

        // Make a local update to the plans array
        const plans = [ ...s.plans ];
        for (const key in Object.keys(plan)){
            plans[s.selectedPlanIdx][Object.keys(plan)[key]] = plan[Object.keys(plan)[key]];
        }

        this.setState({
            plans,
            // editingPlan: null
        });

        // Make a cloud update to the plan
        const res = await api.post('/tpa/plan/update', {
            id: plan._id,
            planDiff: plan
        });

        // If the update was successful, we're done :)
        if (res.success) return;

        toast.error("Failed to save the plan.");

        // Update failed - replace our state with the editing plan data
        plans[s.selectedPlanIdx] = old;
        this.setState({
            plans,
            editingPlan: plan
        });
    }

    // Delete a plan from the cloud if it exists
    deletePlan = async () => {

        this.props.modal("confirm-caution", {
            message: <>Deleting this retirement plan will also delete all <strong>investment accounts</strong> within the plan, and <strong>cannot</strong> be undone.</>,
            action: async() => {
                const s = this.state;

                // Make a copy of the plan we're deleting and the index it's at in case we need to revert because deletion is not successful
                const old = { ...s.plans[s.selectedPlanIdx] };
                const idx = s.selectedPlanIdx;
        
                // Local update
                const plans = [ ...s.plans ];
                plans.splice(idx, 1);
                this.setState({
                    plans,
                    selectedPlanIdx: 0,
                    editingPlan: plans[0],
                    planCount: s.planCount - 1
                });
                // Cloud update
                const res = await api.post('/tpa/plan/remove', {
                    id: old._id
                });

                if (!res.success) {
                    // Update failed - replace our state with the old plan data
                    plans.splice(idx, 0, old);
                    this.setState({
                        plans,
                        selectedPlanIdx: idx,
                        editingPlan: old,
                        planCount: s.planCount + 1
                    });
                    toast.error("Failed to delete the plan.")
                    return
                }

                this.props.modal();
                return
            }
        });
    }


    // Next Page
    nextPage = async () => {
        if (this.state.planPage * 10 + 10 >= this.state.planCount) return

        this.setState({
            planPage: this.state.planPage + 1
        });

        await this.loadPlans(this.state.planPage+1);
    }

    // Load Previous Page of plans
    previousPage = async () => {
        if (this.state.planPage  === 0) return

        this.setState({
            planPage: this.state.planPage - 1
        });

        await this.loadPlans(this.state.planPage-1);
    }

    // Load End Page of plans
    lastPage = async () => {

        if(this.state.planPage===Math.floor((this.state.planCount-1)/10)) return;

        this.setState({
            planPage: Math.floor((this.state.planCount-1)/10)
        })

        await this.loadPlans(Math.floor((this.state.planCount-1)/10))
    }

    // Load First page of plans
    firstPage = async () => {

        if(this.state.planPage===0) return;

        this.setState({
            planPage: 0
        })

        await this.loadPlans()
    }

    // Cancel editing a plan
    cancelPlan = () => {
        // If we're editing an existing plan, overwrite the editingPlan data with the selected plan data
        this.setState({
            editingPlan: this.state.selectedPlanIdx < 0 ? this.state.plans[0] : this.state.plans[this.state.selectedPlanIdx],
            selectedPlanIdx: 0
        });
    }

    // Other non-state manipulators
    // Import a list of plans from an Excel file
    importPlans = async () => {
        this.fileUploadDialog = document.createElement('input');
        this.fileUploadDialog.setAttribute('type', 'file');
        this.fileUploadDialog.setAttribute('multiple', 'multiple');
        this.fileUploadDialog.click();
        this.fileUploadDialog.onchange = async (e) => {
            let path;
            if (e.path)
                path = e.path;
            else if (e.composedPath && e.composedPath())
                path = e.composedPath()
            else
                return; // raise toast error here?
            
 
            const formData = new FormData();
            formData.append("files", path[0].files[0]);
            
            this.props.loading(998, "Just a second...", false, 'planEditor');

            const resImport = await api.post('/tpa/template/import',formData)
 
 
            if(!resImport){
                toast.error("Failed to import the Plans File. Please contact Stax support.")
            }
   
            this.props.updateTeam({isSyncingPlanImport: true}, false);

            this.checkImportStatus();
        }

    }

    // Check the status of the import/sync by polling the API at a regular interval
    checkImportStatus = async () => {

        if(!this.props.team?.isSyncingPlanImport){
            // this.props.loaded()
            return 
        }

        this.props.loading(999, () => {
            return <div>
                Syncing plans...
                <div style={{"paddingTop": 20, "paddingBottom": 40, "fontSize": 20}}>
                    This might take a while,  <a href='/'>click here</a> to go back to your stacks
                </div>
                <div style={{"fontSize": 15}}
                    onClick={this.cancelSync}
                ><span className='a'>Click here to cancel</span></div>
            </div>
        }, false, 'planEditor')
        
        // Poll the api every 10 seconds to see if the import is done 
        this.checkImportInterval = setInterval(async () => {
            const res = await api.get('/tpa/template/status');
            if(res.isSyncingPlanImport)
                return 

            // Syncing is done, clear the interval and reload the plans 
            clearInterval(this.checkImportInterval);
            this.props.updateTeam({isSyncingPlanImport: false}, false)
            await this.loadPlans(); 
            this.props.loaded('planEditor');
        }, 3000)
    }

    // Export a list of plans to an Excel file and send an email
    exportPlans = async (email) => {
        const res = await api.post('/tpa/template/create', {
            email
        });
        if (!res) {
            this.props.modal();
            toast.error("Failed to load plans. ")
            return
        }
        else{
            this.props.modal();
            toast.success("Email will be sent shortly. ")
        }
        
        return
    }

    reportTrustAccountingStatus = async (email, year, internal) => {
        if (email === "" || year === "") {
            this.props.modal();
            toast.error("Please select a year and enter a valid email address.")
            return;
        }
        const res = await api.post('/tpa/template/report', {
            email,
            year,
            internal
        });
        if (!res) {
            this.props.modal();
            toast.error("Failed to report Trust Accounting Status.")
            return
        }
        else{
            this.props.modal();
            toast.success("Email will be sent shortly.")
        }
        return
    }

    // Sync plans from PensionPro (if connected)
    // syncPensionProPlans = async () => {
    //     this.props.loading(9999, "Just a second...", false, 'planEditor');
    //     const res = await api.post('/tpa/pensionpro/sync');
    //     //let waitTeamInterval = setInterval(10000);
    //     if(!res){
    //         toast.error("Failed to sync with PensionPro. Please contact Stax support.")
    //         return
    //     }
    //     else{
    //         // toast.success("Sent request to sync with PensionPro.");
    //         toast.success("Your retirement plans are now syncing to Stax·ai and should be available in 10-15 minutes.")
    //         this.props.updateTeam({isSyncingPlanImport: true}, false);
    //         this.checkImportStatus();
    //     }
    // }

    // Cancel the import or sync process
    cancelSync = async () => {        
        this.props.modal("confirm-caution", {
            action: async() => {
                const res = await api.post('/tpa/template/cancel', {
                    jobType: "tpa-excel-import"
                });
                if (!res) {
                    toast.error("Failed to cancel request. Please contact Stax support.")
                    this.props.updateTeam({isSyncingPlanImport: false}, false);         
                    return
                }
                this.props.modal();
                toast.success("Sent a request to cancel import.")
                this.props.updateTeam({isSyncingPlanImport: false}, false);
                return
            },
            message: "Are you sure you want to cancel the import process? This will not affect any plans that have already been imported."
        });
    }

    // Download excel template to import new plans
    downloadTemplate = async () => {
        const res = await api.download('/tpa/template/get')
        download(res, "TpaPlanTemplate.xlsx");
    }

    // All Plans / CX Review toggle — only when a cx-plans connection exists
    renderCxModeToggle = () => {
        const s = this.state;
        return s.cxStatus?.connected ? <div className="mode-pill-row">
            <div
                className={"mode-pill" + (!s.cxMode ? " mode-pill-active" : "")}
                onClick={() => this.setState({ cxMode: false })}
            >All Plans</div>
            <div
                className={"mode-pill" + (s.cxMode ? " mode-pill-active" : "")}
                onClick={() => this.setState({ cxMode: true, selectedPendingIdx: 0 })}
            >CX Review ({s.cxPending.length})</div>
        </div> : "";
    }

    // CX Review mode: pending rows on the left + selected item detail on the right
    renderCxReviewLayout = () => {
        const s = this.state;
        return <div className="cx-review-layout">
            <div className="plan-editor-list-cards">
                {s.cxPending.length === 0 ? <p style={{
                    width: 300,
                    padding: 20,
                    color: "var(--color-text-light)"
                }}>No pending CX plans to review. <span role="img" aria-label="celebration">🎉</span></p> : ""}

                {s.cxPending.map((row, idx) => <div
                    key={row._id}
                    className={idx === s.selectedPendingIdx ? 'plan-editor-card-open' : ''}
                    onClick={() => this.setState({ selectedPendingIdx: idx })}
                >
                    <div className={idx === s.selectedPendingIdx ? 'plan-editor-card-selected' : 'plan-editor-card'}>
                        <div className='settings-active-format'>
                            <h3 className='plan-editor-card-content'>
                                {row.payload?.name || "Unnamed plan"}
                                <span className='cx-badge-pending'>CX</span>
                            </h3>
                        </div>
                        <p className='plan-editor-card-content'>
                            {(row.payload?.type || "Plan")} · Client: {row.payload?.clientName || "—"}
                        </p>
                    </div>
                </div>)}
            </div>

            <CxReviewPane
                row={s.cxPending[s.selectedPendingIdx]}
                resolveCx={this.resolveCx}
            />
        </div>;
    }

    render = () => {
        const s = this.state;
        const p = this.props;

        return <div className="settings-content">
            {/* Search for plans, accounts, etc. */}
            <Search
                query={s.query}
                search={this.search}
                searching={s.isSearching}
                placeholder="Search plan or account..."
            />

            <div className='plan-editor-content'>
                {/* Plan management table */}
                <div className="plan-editor-header">
                    <h2>Retirement Plans</h2>

                    {/* Add a new plan/sync plans button */}
                    <div className="plan-editor-add-pos">
                    {/* {p.team?.sync?.pensionpro?.length === 0 ? */}
                        {/* // No PensionPro connection - add a new plan */}
                        {!s.cxMode && p.team?.scopes?.includes("TPAPlanEditor") ?
                            <div
                                className="title-icon"
                                onClick={() => this.setState({
                                        editingPlan: newPlan,
                                        selectedPlanIdx: -1,
                                    })
                                }
                            >Add a new plan</div> : null
                        }

                        {/* : (p.team?.sync?.pensionpro?.length > 0 && p.team?.sync?.pensionpro[0]?.skipPPUpdates === false) ? */}
                        {/* PensionPro connection - sync plans*/}
                        {/* <div
                            className="title-icon"
                            style={{ backgroundImage: "url('https://static.stax.ai/images/icons/sync-pensionpro.png')" }}
                            onClick={this.syncPensionProPlans}
                        >Sync with PensionPro</div> : null */}
                    {/* } */}
                    </div>
                    <div className="plan-editor-actions">
                        {s.cxMode && s.cxPending.filter(r => r.suggestion?.exact === true).length > 0 ? <div
                            className="cx-bulk-accept"
                            style={{ float: 'right', marginLeft: 30 }}
                            onClick={async () => {
                                const res = await this.resolveCx({ action: "bulkAcceptSuggested" });
                                if (res?.success) {
                                    const linked = res.linked ?? 0;
                                    const skipped = res.skipped ?? 0;
                                    let msg = "Linked " + linked + " suggested match" + (linked === 1 ? "" : "es");
                                    if (skipped > 0) msg += "; skipped " + skipped + ".";
                                    else msg += ".";
                                    toast.success(msg);
                                }
                            }}
                        >Accept suggested matches ({s.cxPending.filter(r => r.suggestion?.exact === true).length})</div> : ""}
                        {!s.cxMode ? <>
                            {!this.props.team?.scopes?.includes("TPAPlanEditor") ? "" : <div
                                className="plan-editor-action"
                                onClick={this.importPlans}
                                style={{backgroundImage: "url('https://static.stax.ai/images/icons/action-upload.png')"}}
                            >Import plans</div>}
                            {this.state.plans?.length > 0 ? <>
                                <div
                                    className="plan-editor-action"
                                    onClick={() => {
                                        this.props.modal('confirm-email', {
                                            // TODO: Configure this.emailExcel function
                                            action: this.exportPlans
                                        })
                                    }}
                                    style={{backgroundImage: "url('https://static.stax.ai/images/icons/action-export-excel.png')"}}
                                >Export plans</div>
                                {/* <div
                                    className="plan-editor-action"
                                    onClick={() => {
                                        const params = new URLSearchParams();
                                        if(this.state.editingPlan?.internalPlanId)
                                            params.set('filters', JSON.stringify({internalPlanId: [this.state.editingPlan?.internalPlanId]}));
                                        window.open(`/investments/status?${params.toString()}`, '_blank');
                                    }}
                                    style={{backgroundImage: "url('https://static.stax.ai/images/icons/action-check.png')"}}
                                >TA status</div> */}
                                {this.props.team?.ta?.running ? "" : <div
                                    className="plan-editor-action"
                                    onClick={() => this.props.modal('run-ta', {...this.state.editingPlan})}
                                    style={{backgroundImage: "url('https://static.stax.ai/images/icons/action-chart.png')"}}
                                >Run TA</div>}
                            </> : ""}
                        </> : ""}
                    </div>
                    
                </div>
                
                {this.state.planCount === 0 && !this.state.editingPlan ?
                <div>
                    {this.renderCxModeToggle()}
                    {s.cxMode ? <div className="plan-editor-table">
                        {this.renderCxReviewLayout()}
                    </div> : <>
                    <p>You don't have any plans linked to Stax·ai yet. You can get started one of two ways: </p>
                        <ol className='plan-editor-instructions'>
                            {/* <li className='plan-editor-instructions-list'>If you use <b>PensionPro</b>, you can just <a href='/settings/import/pensionpro'>click here to link Stax·ai to your PensionPro account</a> to automatically sync your plan data from there. Any changes you make here will also be automatically pushed to PensionPro as well!</li> */}
                            <li className='plan-editor-instructions-list'>You can <span className='a' onClick={this.downloadTemplate}>click here to download a template</span> as an <b>Excel</b> file, edit it on your computer to input your plans and then <span className='a' onClick={this.importPlans}>click here to re-import it</span> if that's easier. We get it, TPAs love Excel. 😇 </li>
                            <li className='plan-editor-instructions-list'>You can manually add all your plans by clicking on the "Add a new plan" button above.</li>
                        </ol>
                    <p>If you need any help, please reach out to your Customer Success Representative who can walk you through all of this!</p>
                    </>}
                </div>
                : this.state.planCount < 0 && !this.state.editingPlan ?
                    <p style={{
                        marginTop: "17px",
                        padding: "5px",
                        textAlign: "center",
                        color: "var(--color-text-light)",
                        fontSize: "16px"
                    }}>
                    No plans or accounts found matching your search query.
                </p>
                :
                <>
                    <Select
                        className='order-by-dropdown'
                        clearable = {false}
                        placeholder='Sort By'
                        options={options}
                        onChange={async (option)=>{
                            this.setState({
                                filters : {
                                    active: option[0].value
                                }
                            })

                            setTimeout(async () => 
                            {
                                await this.loadPlans()
                            }, 5);
                        }}
                        style={{
                            maxWidth: 200,
                            marginTop: 10,
                            marginBottom: 20,
                            borderRadius: 10
                        }}
                    />

                    {this.state.plans?.length > 0 ? <div className="plan-editor-navigator">
                        <PageNavigator
                            currentPage = {s.planPage}
                            newPlanLength = { s.plans?.length > 10 ? true : false }
                            planLength = {s.plans ? s.planCount : 0}
                            nextPage = {this.nextPage}
                            prevPage = {this.previousPage}
                            firstPage = {this.firstPage}
                            lastpage = {this.lastPage}
                        />
                    </div> : ""}

                    {this.renderCxModeToggle()}

                    <div className="plan-editor-table">
                        {s.cxMode ? this.renderCxReviewLayout() : <>
                            {/* List of plan IDs and name on the left side */}
                            <div className="plan-editor-list-cards">
                                {/* New plan being added? */}
                                {s.editingPlan && !s.editingPlan._id ? <PlanCard
                                    plan={newPlan}
                                    idx={-1}
                                    isNew={true}
                                    selected={true}
                                /> : ""}

                                {/* List of loaded plans */}
                                {s.plans.map((plan, idx) => <PlanCard
                                    key={idx}
                                    idx={idx}
                                    plan={plan}
                                    selected={idx === s.selectedPlanIdx}
                                    onClick={() => {
                                        // If we are actively editing a plan, show ConfirmCaution modal requesting that the user either save the plan or cancel the edit.
                                        // TODO

                                        // If not, we can select the plan
                                        this.setState({
                                            selectedPlanIdx: idx,
                                            editingPlan: plan,
                                            newPlan: null
                                        });
                                    }}
                                />)}
                            </div>

                            {/* Active/selected plan information being editor */}
                            <PlanPage
                                plan={s.editingPlan}
                                editPlanField={this.editPlanField}
                                savePlan={this.savePlan}
                                deletePlan = {this.deletePlan}
                                cancelPlan={this.cancelPlan}
                                modal={this.props.modal}
                                ppConnections={p.team?.sync?.pensionpro ?? []}
                                query={s.query.length > 0? s.query.toLowerCase(): null}
                                team={p.team}
                            />
                        </>}
                    </div>
                </>
                }
            </div>
        </div>;
    }
}